import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { EmptyState } from "@/components/tabdine/Shared";
import { Input } from "@/components/ui/input";
import { FileText } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_OPTIONS = ["all", "new", "confirmed", "preparing", "ready", "served", "paid", "cancelled"];

export default function OrderHistory() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [orderType, setOrderType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status !== "all") p.set("status", status);
    if (orderType !== "all") p.set("order_type", orderType);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    api.get(`/restaurant/order-history?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, orderType, from, to]);

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-6xl">
        <h1 className="font-heading text-2xl font-bold text-brand-dark mb-4">Order History</h1>

        <div className="bg-white rounded-xl border border-brand-dark/5 p-3 mb-4 grid grid-cols-1 sm:grid-cols-6 gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order # / customer" className="sm:col-span-2" data-testid="oh-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded border border-input px-2 text-sm" data-testid="oh-status">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
          </select>
          <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className="h-10 rounded border border-input px-2 text-sm" data-testid="oh-type">
            <option value="all">All types</option>
            <option value="dine_in">Dine-In</option>
            <option value="takeaway">Takeaway</option>
          </select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="oh-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="oh-to" />
        </div>

        {rows.length === 0 ? (
          <EmptyState title="No orders match" subtitle="Try broadening your filters." />
        ) : (
          <div className="bg-white rounded-xl border border-brand-dark/5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-dark/10 text-xs uppercase tracking-widest text-brand-dark/50">
                  <th className="text-left p-3">Order</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Table</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Date</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-b border-brand-dark/5" data-testid={`oh-row-${o.id}`}>
                    <td className="p-3 font-medium text-brand-dark">{o.order_no}</td>
                    <td className="p-3">
                      <div className="text-brand-dark">{o.customer_name || "Guest"}</div>
                      <div className="text-xs text-brand-dark/60">{o.customer_phone}</div>
                    </td>
                    <td className="p-3">{o.order_type === "takeaway" ? "Takeaway" : "Dine-In"}</td>
                    <td className="p-3">{o.table_number ? `T${o.table_number}` : "—"}</td>
                    <td className="p-3 text-right font-heading font-semibold">{inr(o.total_amount)}</td>
                    <td className={`p-3 text-xs uppercase tracking-wide ${o.status === "cancelled" ? "text-red-600" : "text-brand-orange"}`}>
                      {o.status}
                    </td>
                    <td className="p-3 text-xs text-brand-dark/60">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3">
                      {(o.status === "paid" || o.payment_status === "paid") && (
                        <Link to={`/invoice?order=${o.id}&as=restaurant`} target="_blank" rel="noreferrer" className="text-brand-orange text-xs inline-flex items-center gap-1" data-testid={`oh-invoice-${o.id}`}>
                          <FileText size={12} /> Invoice
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </RestaurantLayout>
  );
}
